import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';
import { getStoredUTM } from '../utils/utm';

// Throttle: only ping the backend once every 5 minutes per visitor session.
// The backend dedups per (day, visitor_hash) anyway, but this saves a network
// roundtrip on every internal navigation.
const THROTTLE_MS = 5 * 60 * 1000;
const STORAGE_KEY = 'hb_pv_last';
const REFERRER_SENT_KEY = 'hb_pv_ref_sent';

const isLikelyBot = () => {
  if (typeof navigator === 'undefined') return true;
  const ua = (navigator.userAgent || '').toLowerCase();
  return /bot|crawl|spider|preview|headless|lighthouse|playwright|puppeteer/.test(ua);
};

export default function usePageviewTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (isLikelyBot()) return;

    let lastSent = 0;
    try {
      lastSent = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10) || 0;
    } catch {
      // sessionStorage may be unavailable (private mode) — fall through and send.
    }
    const now = Date.now();
    if (now - lastSent < THROTTLE_MS) return;

    // On the FIRST pageview of a browser session, attach referrer + stored UTM.
    // After the first send we set REFERRER_SENT_KEY so subsequent tickles are lean.
    let referrer = null;
    let utm = null;
    try {
      const alreadySent = sessionStorage.getItem(REFERRER_SENT_KEY) === '1';
      if (!alreadySent) {
        referrer = (typeof document !== 'undefined' && document.referrer) || null;
        // Avoid sending our own domain as external referrer.
        if (referrer && typeof window !== 'undefined') {
          try {
            const refHost = new URL(referrer).host;
            if (refHost && refHost === window.location.host) referrer = null;
          } catch {
            // invalid referrer URL — leave as-is
          }
        }
        utm = getStoredUTM();
        sessionStorage.setItem(REFERRER_SENT_KEY, '1');
      }
    } catch {
      // ignore storage failures
    }

    const payload = { path: pathname };
    if (referrer) payload.referrer = referrer;
    if (utm?.utm_source) payload.utm_source = utm.utm_source;
    if (utm?.utm_medium) payload.utm_medium = utm.utm_medium;
    if (utm?.utm_campaign) payload.utm_campaign = utm.utm_campaign;

    // Fire-and-forget. We don't await, and we swallow errors silently because
    // analytics must never break the user-visible app.
    api.post('/track/pageview', payload).catch(() => {});

    try {
      sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // ignore
    }
  }, [pathname]);
}

